import { CalendarIcon, Download, Search, Shield, X } from "lucide-react";
import { format } from "date-fns";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { cn } from "../../../utils/utils";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import "./ResourceRequests.css";

interface ResourceRequestFiltersProps {
  searchTerm: string;
  statusFilter: string;
  privilegeFilter: string;
  startDate?: Date;
  endDate?: Date;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onPrivilegeChange: (value: string) => void;
  onStartDateChange: (value: Date | undefined) => void;
  onEndDateChange: (value: Date | undefined) => void;
  onReset: () => void;
  onDownloadReport: () => void;
  showDownload?: boolean;
  showSearch?: boolean;
  includeCloudStatus?: boolean;
  idPrefix?: string;
}

export default function ResourceRequestFilters({
  searchTerm,
  statusFilter,
  privilegeFilter,
  startDate,
  endDate,
  onSearchChange,
  onStatusChange,
  onPrivilegeChange,
  onStartDateChange,
  onEndDateChange,
  onReset,
  onDownloadReport,
  showDownload = true,
  showSearch = true,
  includeCloudStatus = true,
  idPrefix = "resource-request",
}: ResourceRequestFiltersProps) {
  const isResetDisabled =
    !searchTerm &&
    !startDate &&
    !endDate &&
    (!statusFilter || statusFilter === "all") &&
    privilegeFilter === "all";

  return (
    <div className="resource-request-filters">
      {showSearch && (
        <div className="resource-request-filter resource-request-search">
          <label htmlFor={`${idPrefix}-search`}>
            <Search />
            Search
          </label>
          <div className="relative">
            <Search className="resource-request-search-icon" />
            <Input
              id={`${idPrefix}-search`}
              placeholder="Search requests..."
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
              className="pl-10"
              data-testid="input-search"
            />
          </div>
        </div>
      )}
      <div className="resource-request-filter">
        <label>
          <CalendarIcon />
          Start date
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              aria-label="Start date"
              className={cn(
                "resource-request-date",
                !startDate && "text-muted-foreground",
              )}
              data-testid="button-start-date"
            >
              <CalendarIcon className="w-4 h-4" />
              {startDate ? format(startDate, "MMM dd") : "Start date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={(date) => {
                onStartDateChange(date);
                onEndDateChange(undefined);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="resource-request-filter">
        <label>
          <CalendarIcon />
          End date
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              aria-label="End date"
              className={cn(
                "resource-request-date",
                !endDate && "text-muted-foreground",
              )}
              data-testid="button-end-date"
            >
              <CalendarIcon className="w-4 h-4" />
              {endDate ? format(endDate, "MMM dd") : "End date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={onEndDateChange}
              initialFocus
              disabled={(date) => !startDate || date <= startDate}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="resource-request-filter">
        <label htmlFor={`${idPrefix}-status`}>
          <Shield />
          Status
        </label>
        <Select value={statusFilter} onValueChange={onStatusChange}>
          <SelectTrigger id={`${idPrefix}-status`}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="DECLINE">Declined</SelectItem>
            <SelectItem value="Expired">Expired</SelectItem>
            <SelectItem value="Revoked">Revoked</SelectItem>
            {includeCloudStatus && (
              <SelectItem value="CLOUD_UNCONFIGURED">
                Cloud Unconfigured
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="resource-request-filter">
        <label htmlFor={`${idPrefix}-privilege`}>
          <Shield />
          Privilege level
        </label>
        <Select value={privilegeFilter} onValueChange={onPrivilegeChange}>
          <SelectTrigger id={`${idPrefix}-privilege`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All privileges</SelectItem>
            <SelectItem value="DYNAMIC">Dynamic</SelectItem>
            <SelectItem value="PERMANENT">Permanent</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="resource-request-filter-actions">
        <Button
          variant="outline"
          onClick={onReset}
          disabled={isResetDisabled}
          className="resource-request-reset"
          data-testid="button-reset-search"
        >
          <X className="w-4 h-4" />
          Reset
        </Button>
        {showDownload && (
          <EndpointActionButton
            onClick={onDownloadReport}
            data-testid="button-download-report"
          >
            <Download className="w-4 h-4" />
            Download Report
          </EndpointActionButton>
        )}
      </div>
    </div>
  );
}
